'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { CategoryNavigationMenu } from '@diritto/common/navigation/constant';
import {
  ExploreNavigationMenu,
  ExploreSubMenu,
  getExploreSubMenus,
  getFilterAndOrderTypeByExplore,
} from '@diritto/common/navigation/explore';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { extractEnumValue } from '@diritto/common/utils/enum';
import { Color } from '@diritto/common/style/color';
import { useTrackingState } from 'contexts/TrackingProvider';
import { ExploreCategoryNavigationBar } from 'app-components/explore/ExploreCategoryNavigationBar';
import { ExploreContentList } from 'app-components/explore/ExploreContentList';
import { ExploreNavigationBar } from 'app-components/explore/ExploreNavigationBar';
import { ExploreSubTab } from 'app-components/explore/ExploreSubTab';
import { Divider } from 'ui-components/common/Divider';
import { StyledTopMenu } from 'ui-components/navigations/styles';
import { InfoFilledIcon } from 'icons/InfoFilledIcon';
import { useHomeSettingState } from 'contexts/HomeSettingProvider';

interface ExplorePageProps {
  categoryId?: number;
  categoryNavMenu: CategoryNavigationMenu;
  exploreMenu: ExploreNavigationMenu;
}

export const ExplorePage = ({ categoryId, categoryNavMenu, exploreMenu }: ExplorePageProps) => {
  const { tracker } = useTrackingState();
  const searchParams = useSearchParams();
  const { categoryIds: favoriteCategoryIds } = useHomeSettingState();

  const exploreSubMenu = useMemo(
    () =>
      extractEnumValue(
        ExploreSubMenu,
        String(searchParams?.get('exploreSubMenu')),
        getExploreSubMenus(exploreMenu)[0]?.exploreSubMenu,
      ),
    [exploreMenu, searchParams],
  );

  useEffect(() => {
    tracker.track(TrackingEventType.ViewExplorer, { exploreMenu, categoryMenu: categoryNavMenu, exploreSubMenu });
  }, [categoryNavMenu, exploreMenu, exploreSubMenu, tracker]);

  const { filterType, orderType } = useMemo(
    () => getFilterAndOrderTypeByExplore(exploreMenu, exploreSubMenu),
    [exploreMenu, exploreSubMenu],
  );

  const renderFavoriteCategoryBar = useCallback(() => {
    if (categoryNavMenu !== CategoryNavigationMenu.All || favoriteCategoryIds.length === 0) {
      return null;
    }

    return (
      <div className="inline-flex justify-start items-center h-12 px-4 gap-1 bg-slate-50 overflow-hidden">
        <InfoFilledIcon className="w-4 h-4" color={Color.Gray600} />
        <div className="justify-start text-gray-700 text-xxxs font-medium">
          선호 장르에 맞춰 전체 탭이 구성되었어요.
        </div>
      </div>
    );
  }, [favoriteCategoryIds, categoryId]);

  return (
    <>
      <StyledTopMenu>
        <ExploreNavigationBar selectedMenu={exploreMenu} />
        <Divider />
      </StyledTopMenu>
      <StyledTopMenu>
        <ExploreCategoryNavigationBar exploreMenu={exploreMenu} selectedMenu={categoryNavMenu} />
        <Divider />
      </StyledTopMenu>
      {renderFavoriteCategoryBar()}
      <ExploreSubTab
        className="flex justify-center my-4 mb-2"
        exploreMenu={exploreMenu}
        categoryNavMenu={categoryNavMenu}
        selectSubMenu={exploreSubMenu}
      />
      <div className="m-4">
        <ExploreContentList
          categoryId={categoryId}
          categoryIds={favoriteCategoryIds}
          orderType={orderType}
          filterType={filterType}
          exploreMenu={exploreMenu}
          categoryNavMenu={categoryNavMenu}
          exploreSubMenu={exploreSubMenu}
        />
      </div>
    </>
  );
};
